import type { FeedLog, FishStockLog, MortalityLog, WeightSampleLog } from '../../types/production'
import { retryEntityBatchSyncStatus, type EntityBatchSyncStatus } from '../../utils/entityMutationSync'
import { farmDayKey } from '../../utils/farmTime'
import { feedLogApi } from '../../utils/feedLogApi'
import { fishStockLogApi } from '../../utils/fishStockLogApi'
import { mortalityLogApi } from '../../utils/mortalityLogApi'
import {
  listProductionUnitTableRows,
  retryProductionUnitTableRowSyncStatus,
  upsertProductionUnitTableRowsAndSync,
  type BiomassDataRow
} from '../../utils/productionUnitTable'
import { weightSampleLogApi } from '../../utils/weightSampleLogApi'
import { buildBiomassFishStockMetadata } from '../biomass/fishStockLogBridge'
import { buildBiomassSamplingMortalityMetadata } from '../biomass/mortalityLogBridge'
import { buildBiomassSamplingWeightMetadata } from '../biomass/weightSampleLogBridge'
import type { DailySheetLine } from './dailySheet'
import { dailySheetRecordId } from './dailySheetIds'

// Findings #199: saves the checked lines of a daily sheet the way the log
// screens save them — the day's Biomasa row of the lot (reused when it exists)
// and one record per value, linked to that row. Ids come from the line itself,
// so the same file imported twice updates instead of adding.

export const DAILY_SHEET_SOURCE = 'dailySheetImport'

export type DailySheetSaveResult = {
  rows: number
  counts: number
  weights: number
  mortalities: number
  feeds: number
  sync: EntityBatchSyncStatus[]
}

type FacilityContext = {
  farmId: string
  farmName?: string
  timeZone: string
  unitName: (unitId: string) => string
}

export const saveDailySheetLines = async (lines: readonly DailySheetLine[], facility: FacilityContext): Promise<DailySheetSaveResult> => {
  const ready = lines.filter((line) => (
    line.errors.length === 0 && line.records.length > 0 && line.day && line.unitId && line.lotId && line.lotKey && line.recordedAt
  ))
  const importedAt = new Date().toISOString()
  const fishStockLogs: FishStockLog[] = []
  const weightLogs: WeightSampleLog[] = []
  const mortalityLogs: MortalityLog[] = []
  const feedLogs: FeedLog[] = []
  const rowIds: string[] = []

  const byUnit = new Map<string, DailySheetLine[]>()
  ready.forEach((line) => {
    const list = byUnit.get(line.unitId as string) ?? []
    list.push(line)
    byUnit.set(line.unitId as string, list)
  })

  for (const [unitId, unitLines] of byUnit) {
    const context = {
      farmId: facility.farmId,
      farmName: facility.farmName,
      productionUnitId: unitId,
      productionUnitName: facility.unitName(unitId)
    }
    // eslint-disable-next-line no-await-in-loop
    const existingRows = await listProductionUnitTableRows(context).catch(() => [] as BiomassDataRow[])
    const rows: BiomassDataRow[] = []

    for (const line of unitLines) {
      const day = line.day as string
      const lotId = line.lotId as string
      const lotKey = line.lotKey as string
      const recordedAt = line.recordedAt as string
      const sameDay = existingRows.find((row) => (
        String(row.lotId ?? '') === lotId && farmDayKey(String(row.date ?? ''), facility.timeZone) === day
      ))
      // eslint-disable-next-line no-await-in-loop
      const rowId = sameDay ? String(sameDay.id) : await dailySheetRecordId({ farmId: facility.farmId, unitId, lotId, day, kind: 'row' })
      const count = line.records.find((record) => record.kind === 'count')
      const base: BiomassDataRow = sameDay ?? {
        id: rowId,
        date: recordedAt,
        lotId,
        lot: lotKey,
        stage: 'Active',
        weight: '',
        dailyMortality: '',
        fishStock: '',
        upVol: '',
        health: '',
        healthDetail: ''
      } as BiomassDataRow
      rows.push(count && count.kind === 'count'
        ? { ...base, id: rowId, fishStock: String(count.value), stockMethod: count.method === 'estimation' ? 'estimate' : 'exact' }
        : { ...base, id: rowId })
      rowIds.push(rowId)

      const common = { farmId: facility.farmId, productionUnitId: unitId, lot: lotKey, lotId, recordedAt, notes: line.notes }
      const source = { source: DAILY_SHEET_SOURCE, importedAt, sheetLine: line.lineNumber }
      for (const record of line.records) {
        // eslint-disable-next-line no-await-in-loop
        const id = await dailySheetRecordId({ farmId: facility.farmId, unitId, lotId, day, kind: record.kind })
        if (record.kind === 'count') {
          fishStockLogs.push({
            ...common,
            id,
            productionRowId: rowId,
            countedStock: record.value,
            method: record.method,
            metadata: { ...buildBiomassFishStockMetadata({ productionRowId: rowId, lotId, stage: 'Active' }), ...source }
          })
        } else if (record.kind === 'weight') {
          weightLogs.push({
            ...common,
            id,
            productionRowId: rowId,
            weightGrams: String(record.value),
            metadata: { ...buildBiomassSamplingWeightMetadata({ productionRowId: rowId, lotId, stage: 'Active' }), ...source }
          })
        } else if (record.kind === 'mortality') {
          mortalityLogs.push({
            ...common,
            id,
            mortalityCount: String(record.value),
            metadata: { ...buildBiomassSamplingMortalityMetadata({ productionRowId: rowId, lotId, stage: 'Active' }), productionRowId: rowId, ...source }
          })
        } else {
          feedLogs.push({
            ...common,
            id,
            feedKg: String(record.value),
            metadata: { productionRowId: rowId, ...source }
          })
        }
      }
    }

    // eslint-disable-next-line no-await-in-loop
    await upsertProductionUnitTableRowsAndSync(context, rows, { syncRowIds: rows.map((row) => String(row.id)) })
  }

  // Rows first, so each record finds its day row on the server.
  await fishStockLogApi.saveImportedLogs(fishStockLogs)
  await weightSampleLogApi.saveImportedLogs(weightLogs)
  await mortalityLogApi.saveImportedLogs(mortalityLogs)
  await feedLogApi.saveImportedLogs(feedLogs)

  const sync: EntityBatchSyncStatus[] = []
  sync.push(await retryProductionUnitTableRowSyncStatus(rowIds))
  const pushes: Array<[Parameters<typeof retryEntityBatchSyncStatus>[0]['entityType'], string[]]> = [
    ['fishStockLog', fishStockLogs.map((log) => log.id)],
    ['weightSampleLog', weightLogs.map((log) => log.id)],
    ['mortalityLog', mortalityLogs.map((log) => log.id)],
    ['feedLog', feedLogs.map((log) => log.id)]
  ]
  for (const [entityType, entityIds] of pushes) {
    if (entityIds.length === 0) continue
    // eslint-disable-next-line no-await-in-loop
    sync.push(await retryEntityBatchSyncStatus({ entityType, entityIds, maxEntries: 200, maxPasses: 10 }))
  }

  return {
    rows: rowIds.length,
    counts: fishStockLogs.length,
    weights: weightLogs.length,
    mortalities: mortalityLogs.length,
    feeds: feedLogs.length,
    sync
  }
}
